import * as moment from 'moment';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    Box,
    Container,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Skeleton,
    Typography,
} from '@mui/material';
import {DatePicker} from '@mui/x-date-pickers';
import {
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import {useGetAiCostsMetricsQuery} from '@src/modules/aiCosts/aiCosts.api';
import {theme} from '@src/utils/theme';
import {InfoCard} from '@src/screens/GeneralDashboard/InfoCard';

import styles from './ai-costs-dashboard.module.scss';

const REQUEST_DATE_FORMAT = 'YYYY-MM-DD';

const MODEL_I18N_KEYS = ['claude-3-5-haiku', 'claude-3-5-sonnet', 'claude-3-opus', 'gpt-4', 'gpt-3-5-turbo'];

function getFeatureDisplayName(t, featureKey) {
    if (!featureKey || featureKey === 'Desconhecido') {
        return t('AI_COSTS.UNKNOWN');
    }
    const translated = t(`AI_COSTS.FEATURES.${featureKey}`);
    return translated !== `AI_COSTS.FEATURES.${featureKey}` ? translated : featureKey;
}

function getModelDisplayName(t, modelId) {
    if (!modelId || modelId === 'Desconhecido') {
        return t('AI_COSTS.UNKNOWN');
    }
    const key = MODEL_I18N_KEYS.find(k => modelId.toLowerCase().includes(k));
    if (key) {
        const translated = t(`AI_COSTS.MODELS.${key}`);
        return translated !== `AI_COSTS.MODELS.${key}` ? translated : modelId;
    }
    return modelId;
}

function formatCost(value) {
    if (typeof value === 'string') {
        value = parseFloat(value);
    }
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    }).format(value || 0);
}

function formatNumber(value) {
    return new Intl.NumberFormat('pt-BR').format(value || 0);
}

function CustomBarTooltip({active, payload}) {
    const {t} = useTranslation();
    if (active && payload && payload.length) {
        const item = payload[0].payload;
        return (
            <div className={styles.customTooltip}>
                <p className={styles.tooltipLabel}>{item.name}</p>
                <p className={styles.tooltipValue}>
                    {t('AI_COSTS.REQUESTS')}: <strong>{formatNumber(item.requests)}</strong>
                </p>
                <p className={styles.tooltipValue}>
                    {t('AI_COSTS.COST')}: <strong>{formatCost(item.cost)}</strong>
                </p>
            </div>
        );
    }
    return null;
}

function CustomPieTooltip({active, payload}) {
    const {t} = useTranslation();
    if (active && payload && payload.length) {
        const item = payload[0];
        const percent = item.percent ?? item.payload?.percent ?? 0;
        const percentLabel = (percent * 100).toFixed(1);
        return (
            <div className={styles.customTooltip}>
                <p className={styles.tooltipLabel}>{item.name}</p>
                <p className={styles.tooltipValue}>
                    {t('AI_COSTS.TOOLTIP_COST')}: <strong>{formatCost(item.value)}</strong>
                </p>
                <p className={styles.tooltipPercentage}>
                    {t('AI_COSTS.TOOLTIP_PERCENT_OF_TOTAL')}: <strong>{percentLabel}%</strong>
                </p>
            </div>
        );
    }
    return null;
}

export default function AiCostsDashboard() {
    const {t} = useTranslation();
    const [dateFrom, setDateFrom] = useState(moment().subtract(7, 'days').startOf('day'));
    const [dateTo, setDateTo] = useState(moment().endOf('day'));
    const [rangeDays, setRangeDays] = useState(7);
    const [isFromOpen, setIsFromOpen] = useState(false);
    const [isToOpen, setIsToOpen] = useState(false);

    const {data, isLoading, isError} = useGetAiCostsMetricsQuery(
        {
            dateFrom: moment(dateFrom).format(REQUEST_DATE_FORMAT),
            dateTo: moment(dateTo).format(REQUEST_DATE_FORMAT),
            view: 'summary',
        },
        {refetchOnMountOrArgChange: true},
    );

    const handleRangeChange = (days) => {
        setRangeDays(days);
        setDateFrom(moment().subtract(days, 'days').startOf('day'));
        setDateTo(moment().endOf('day'));
    };

    const items = data?.items ?? [];

    const totalRequests = items.reduce((sum, item) => sum + (item.request_count || 0), 0);
    const totalCost = items.reduce((sum, item) => sum + parseFloat(item.total_cost_units || 0), 0);
    const totalTokens = items.reduce((sum, item) => sum + (item.total_tokens || 0), 0);

    const featureData = items.reduce((acc, item) => {
        const feature = item.feature || 'Desconhecido';
        if (!acc[feature]) {
            acc[feature] = {name: feature, requests: 0, cost: 0};
        }
        acc[feature].requests += item.request_count || 0;
        acc[feature].cost += parseFloat(item.total_cost_units || 0);
        return acc;
    }, {});

    const featureChartData = Object.values(featureData).map(item => ({
        ...item,
        name: getFeatureDisplayName(t, item.name),
    }));

    const modelData = items.reduce((acc, item) => {
        const model = item.model_id || 'Desconhecido';
        if (!acc[model]) {
            acc[model] = {name: model, requests: 0, cost: 0};
        }
        acc[model].requests += item.request_count || 0;
        acc[model].cost += parseFloat(item.total_cost_units || 0);
        return acc;
    }, {});

    const modelChartData = Object.values(modelData).map(item => ({
        ...item,
        name: getModelDisplayName(t, item.name),
    }));

    const topFeaturesByCost = [...featureChartData]
        .sort((a, b) => b.cost - a.cost)
        .slice(0, 5);

    return (
        <Container maxWidth='xl'>
            <header className={styles.header}>
                <Typography variant='h4' component='h2'>
                    {t('AI_COSTS.TITLE')}
                </Typography>
            </header>
            <main className={styles.main}>
                <div className={styles.filters}>
                    <FormControl size='small'>
                        <DatePicker
                            value={dateFrom}
                            onChange={(date) => setDateFrom(date)}
                            label={t('AI_COSTS.FROM')}
                            maxDate={moment(dateTo).startOf('day')}
                            sx={{width: 150}}
                            open={isFromOpen}
                            onOpen={() => setIsFromOpen(true)}
                            onClose={() => setIsFromOpen(false)}
                            slotProps={{
                                textField: {
                                    inputProps: {readOnly: true},
                                    onClick: () => setIsFromOpen(true),
                                },
                            }}
                        />
                    </FormControl>
                    <FormControl size='small'>
                        <DatePicker
                            value={dateTo}
                            onChange={(date) => setDateTo(date)}
                            label={t('AI_COSTS.TO')}
                            minDate={moment(dateFrom).endOf('day')}
                            maxDate={moment().endOf('day')}
                            sx={{width: 150}}
                            open={isToOpen}
                            onOpen={() => setIsToOpen(true)}
                            onClose={() => setIsToOpen(false)}
                            slotProps={{
                                textField: {
                                    inputProps: {readOnly: true},
                                    onClick: () => setIsToOpen(true),
                                },
                            }}
                        />
                    </FormControl>
                    <FormControl size='small' sx={{minWidth: 140}}>
                        <InputLabel id='ai-costs-range'>{t('AI_COSTS.RANGE_DAYS')}</InputLabel>
                        <Select
                            labelId='ai-costs-range'
                            label={t('AI_COSTS.RANGE_DAYS')}
                            value={rangeDays}
                            onChange={(e) => handleRangeChange(e.target.value)}
                            className={styles.aiRangeSelect}
                        >
                            <MenuItem value={7}>{t('AI_COSTS.LAST_7_DAYS')}</MenuItem>
                            <MenuItem value={15}>{t('AI_COSTS.LAST_15_DAYS')}</MenuItem>
                            <MenuItem value={30}>{t('AI_COSTS.LAST_30_DAYS')}</MenuItem>
                        </Select>
                    </FormControl>
                </div>

                {isError && (
                    <Typography color='error' sx={{mb: 2}}>
                        {t('ERROR_TABLE_LOAD')}
                    </Typography>
                )}

                <div className={styles.cardsRow}>
                    {isLoading ? (
                        <>
                            <Skeleton variant='rounded' width='100%' height={114} />
                            <Skeleton variant='rounded' width='100%' height={114} />
                            <Skeleton variant='rounded' width='100%' height={114} />
                        </>
                    ) : (
                        <>
                            <InfoCard
                                name={t('AI_COSTS.TOTAL_REQUESTS')}
                                total={totalRequests}
                            />
                            <Box className={styles.costCard}>
                                <Typography className={styles.costCardLabel}>
                                    {t('AI_COSTS.TOTAL_COST')}
                                </Typography>
                                <Typography variant='h5' className={styles.costCardValue}>
                                    {formatCost(totalCost)}
                                </Typography>
                            </Box>
                            <Box className={styles.costCard}>
                                <Typography className={styles.costCardLabel}>
                                    {t('AI_COSTS.TOTAL_TOKENS')}
                                </Typography>
                                <Typography variant='h5' className={styles.costCardValue}>
                                    {formatNumber(totalTokens)}
                                </Typography>
                            </Box>
                        </>
                    )}
                </div>

                <div className={styles.chartsGrid}>
                    <div className={styles.chartSection}>
                        <Typography variant='h6' className={styles.chartTitle}>
                            {t('AI_COSTS.REQUESTS_BY_FEATURE')}
                        </Typography>
                        <div className={styles.chartContainer}>
                            {isLoading ? (
                                <Skeleton variant='rounded' width='100%' height={350} />
                            ) : featureChartData.length > 0 ? (
                                <ResponsiveContainer width='100%' height={350}>
                                    <BarChart
                                        data={featureChartData}
                                        margin={{top: 20, right: 30, left: 20, bottom: 60}}
                                    >
                                        <CartesianGrid strokeDasharray='3 3' stroke='#374151' opacity={0.3} />
                                        <XAxis
                                            dataKey='name'
                                            stroke='#9CA3AF'
                                            angle={0}
                                            textAnchor='middle'
                                            height={60}
                                            style={{fontSize: '11px', fontWeight: 500}}
                                        />
                                        <YAxis
                                            stroke='#9CA3AF'
                                            style={{fontSize: '12px', fontWeight: 500}}
                                            allowDecimals={false}
                                        />
                                        <Tooltip content={<CustomBarTooltip />} />
                                        <Legend wrapperStyle={{paddingTop: '20px'}} />
                                        <Bar
                                            dataKey='requests'
                                            fill={theme.palette.primary.main}
                                            name={t('AI_COSTS.REQUESTS')}
                                            radius={[8, 8, 0, 0]}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <Typography color='textSecondary' sx={{textAlign: 'center', py: 4}}>
                                    {t('NO_DATA_FOUND')}
                                </Typography>
                            )}
                        </div>
                    </div>

                    <div className={styles.chartSection}>
                        <Typography variant='h6' className={styles.chartTitle}>
                            {t('AI_COSTS.COST_DISTRIBUTION')}
                        </Typography>
                        <div className={styles.chartContainer}>
                            {isLoading ? (
                                <Skeleton variant='rounded' width='100%' height={350} />
                            ) : topFeaturesByCost.length > 0 ? (
                                <ResponsiveContainer width='100%' height={350}>
                                    <PieChart>
                                        <Pie
                                            data={topFeaturesByCost}
                                            cx='50%'
                                            cy='50%'
                                            labelLine={false}
                                            label={({name, percent}) =>
                                                `${name}: ${(percent * 100).toFixed(1)}%`
                                            }
                                            outerRadius={100}
                                            fill='#8884d8'
                                            dataKey='cost'
                                        >
                                            {topFeaturesByCost.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={theme.palette.chart[index % theme.palette.chart.length]}
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip content={<CustomPieTooltip />} />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <Typography color='textSecondary' sx={{textAlign: 'center', py: 4}}>
                                    {t('NO_DATA_FOUND')}
                                </Typography>
                            )}
                        </div>
                    </div>

                    <div className={styles.chartSection}>
                        <Typography variant='h6' className={styles.chartTitle}>
                            {t('AI_COSTS.REQUESTS_BY_MODEL')}
                        </Typography>
                        <div className={styles.chartContainer}>
                            {isLoading ? (
                                <Skeleton variant='rounded' width='100%' height={350} />
                            ) : modelChartData.length > 0 ? (
                                <ResponsiveContainer width='100%' height={350}>
                                    <BarChart
                                        data={modelChartData}
                                        margin={{top: 20, right: 30, left: 20, bottom: 60}}
                                    >
                                        <CartesianGrid strokeDasharray='3 3' stroke='#374151' opacity={0.3} />
                                        <XAxis
                                            dataKey='name'
                                            stroke='#9CA3AF'
                                            angle={0}
                                            textAnchor='middle'
                                            height={60}
                                            style={{fontSize: '11px', fontWeight: 500}}
                                        />
                                        <YAxis
                                            stroke='#9CA3AF'
                                            style={{fontSize: '12px', fontWeight: 500}}
                                            allowDecimals={false}
                                        />
                                        <Tooltip content={<CustomBarTooltip />} />
                                        <Legend wrapperStyle={{paddingTop: '20px'}} />
                                        <Bar
                                            dataKey='requests'
                                            fill={theme.palette.secondary.main}
                                            name={t('AI_COSTS.REQUESTS')}
                                            radius={[8, 8, 0, 0]}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <Typography color='textSecondary' sx={{textAlign: 'center', py: 4}}>
                                    {t('NO_DATA_FOUND')}
                                </Typography>
                            )}
                        </div>
                    </div>
                </div>
            </main>
        </Container>
    );
}
